import { useEffect, useState } from "react";
import { Loader2, Lock, LockOpen, History, Save, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LineItems from "@/components/LineItems";

const toHm = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}`;
};
const fromHm = (v) => {
  const [h, m] = String(v || "0:0").split(":");
  return (parseInt(h, 10) || 0) * 3600 + (parseInt(m, 10) || 0) * 60;
};

const TimeField = ({ label, value, onChange, testid }) => (
  <div>
    <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{label}</p>
    <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder="tt:mm" className="mt-1 h-10" />
  </div>
);

export default function FinishedEditPanel({ job, summary, staff = [], leader, onSaved, simple }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [form, setForm] = useState(null);

  const unlocked = !!job.unlocked_for_edit && !!form;

  const history = job.edit_history || [];

  const buildForm = () => {
    const ids = Array.from(new Set([
      ...(job.workflows || []).map((w) => w.user_id),
      ...(job.assigned_to || []),
    ]));
    return {
      mil: job.distance_km != null ? (job.distance_km / 10).toFixed(1) : "",
      materials: job.materials || [],
      spare_parts: job.spare_parts || [],
      others: job.others || [],
      users: ids.map((uid) => {
        const w = (job.workflows || []).find((x) => x.user_id === uid) || {};
        return {
          user_id: uid,
          user_name:
            (summary?.per_user || []).find((p) => p.user_id === uid)?.user_name ||
            staff.find((s) => s.id === uid)?.name ||
            t("finish.technician"),
          work: toHm(w.work_seconds),
          travel_out: toHm(w.travel_out_seconds),
          travel_home: toHm(w.travel_home_seconds),
        };
      }),
    };
  };

  // Redan upplåst (t.ex. efter omladdning) → visa formuläret direkt
  useEffect(() => {
    if (leader && job.unlocked_for_edit && !form) setForm(buildForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.unlocked_for_edit, leader]);

  const startEdit = async () => {
    if (!window.confirm(t("finish.unlockConfirm"))) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/unlock`);
      setForm(buildForm());
      await onSaved?.();
      toast.success(t("finish.unlocked"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/lock`);
      setForm(null);
      await onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const mil = form.mil === "" ? null : parseFloat(String(form.mil).replace(",", "."));
      await api.put(`/jobs/${job.id}/finished-values`, {
        distance_km: mil == null || Number.isNaN(mil) ? null : Math.round(mil * 10 * 10) / 10,
        materials: form.materials,
        spare_parts: form.spare_parts,
        others: form.others,
        per_user: form.users.map((u) => ({
          user_id: u.user_id,
          work_seconds: fromHm(u.work),
          travel_out_seconds: fromHm(u.travel_out),
          travel_home_seconds: fromHm(u.travel_home),
        })),
      });
      setForm(null);
      await onSaved?.();
      toast.success(t("finish.saved"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setUser = (i, key, v) =>
    setForm((f) => ({ ...f, users: f.users.map((u, idx) => (idx === i ? { ...u, [key]: v } : u)) }));

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="finished-edit-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex-1 text-xl font-semibold text-slate-800">
          {job.locked || job.status === "done" ? t("finish.title") : t("finish.correctTitle")}
        </h2>
        {history.length > 0 && (
          <Button
            data-testid="edit-history-toggle"
            variant="outline"
            onClick={() => setShowHistory((s) => !s)}
            className="h-10 bg-white transition-colors duration-200 hover:bg-slate-100"
          >
            <History className="mr-1.5 h-4 w-4" /> {t("finish.history")} ({history.length})
          </Button>
        )}
        {leader && !unlocked && (
          <Button
            data-testid="unlock-for-edit-button"
            disabled={busy}
            onClick={startEdit}
            className="h-11 w-full bg-amber-600 px-5 text-base font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-amber-700 sm:w-auto"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LockOpen className="mr-2 h-5 w-5" /> Lås upp för redigering</>}
          </Button>
        )}
      </div>

      {!leader && (
        <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500" data-testid="finished-locked-note">
          <Lock className="h-3.5 w-3.5" /> {t("finish.notLeaderNote")}
        </p>
      )}

      {unlocked && (
        <div className="mt-3 space-y-3" data-testid="finished-edit-form">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {t("finish.editMode")}
          </div>

          {!simple && (
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{t("finish.distanceMil")}</p>
            <Input
              data-testid="edit-distance-input"
              value={form.mil}
              onChange={(e) => setForm((f) => ({ ...f, mil: e.target.value }))}
              placeholder="t.ex. 4,5"
              className="mt-1 h-10"
            />
            <p className="mt-1 text-xs text-slate-500">
              {t("finish.roundTrip", { value: form.mil ? (parseFloat(String(form.mil).replace(",", ".")) * 2 || 0).toFixed(1) : "0.0" })}
            </p>
          </div>
          )}

          {!simple && form.users.map((u, i) => (
            <div key={u.user_id} className="rounded-md border border-slate-200 p-3" data-testid={`edit-user-${u.user_id}`}>
              <p className="text-sm font-semibold text-slate-900">{u.user_name}</p>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <TimeField label={t("finish.workTime")} value={u.work} onChange={(v) => setUser(i, "work", v)} testid={`edit-work-${u.user_id}`} />
                <TimeField label={t("finish.travelOut")} value={u.travel_out} onChange={(v) => setUser(i, "travel_out", v)} testid={`edit-travel-out-${u.user_id}`} />
                <TimeField label={t("finish.travelHome")} value={u.travel_home} onChange={(v) => setUser(i, "travel_home", v)} testid={`edit-travel-home-${u.user_id}`} />
              </div>
            </div>
          ))}

          {!simple && (
          <>
          <LineItems label={t("wo.materials")} testid="edit-materials" category="material" items={form.materials} onChange={(v) => setForm((f) => ({ ...f, materials: v }))} />
          <LineItems label={t("wo.spareParts")} testid="edit-spare-parts" category="spare_part" items={form.spare_parts} onChange={(v) => setForm((f) => ({ ...f, spare_parts: v }))} />
          <LineItems label={t("wo.other")} testid="edit-others" category="other" items={form.others} onChange={(v) => setForm((f) => ({ ...f, others: v }))} />
          </>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="finished-edit-save"
              disabled={busy}
              onClick={save}
              className="h-11 flex-1 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> {t("finish.saveChanges")}</>}
            </Button>
            <Button
              data-testid="finished-edit-cancel"
              disabled={busy}
              variant="outline"
              onClick={cancel}
              className="h-11 bg-white transition-colors duration-200 hover:bg-slate-100"
            >
              <X className="mr-1.5 h-4 w-4" /> {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="mt-3 space-y-2" data-testid="edit-history-list">
          {history.slice().reverse().map((h) => (
            <div key={h.id} className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-sm" data-testid={`edit-history-${h.id}`}>
              <p className="font-medium text-slate-900">{h.label}</p>
              <p className="text-slate-700">
                {h.old_value != null && <span className="line-through text-slate-500">{h.old_value}</span>}
                {h.old_value != null && " → "}
                <b>{h.new_value}</b>
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {h.user_name || "–"} · {(h.created_at || "").slice(0, 16).replace("T", " ")}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
